/**
 * Landing Page
 *
 * Public landing page for unauthenticated users.
 * Displays ModernPath branding and sign-in button that initiates OIDC login.
 * Redirects authenticated users to /users.
 *
 * @module pages/LandingPage
 */

import { Navigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import { useAuthStore } from '../stores/useAuthStore';
import { ROUTES } from '../routes/constants';
import { formatErrorMessage } from '../utils';

/**
 * LandingPage - Public landing page for unauthenticated users.
 *
 * Features:
 * - ModernPath branding/logo
 * - Welcome text and tagline
 * - Prominent "Sign in" button that initiates OIDC login
 * - Loading state during login request
 * - Error display with retry capability
 * - Auto-redirect to /users if already authenticated
 */
export function LandingPage() {
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const login = useAuthStore((state) => state.login);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);

  // If already authenticated, redirect to /users
  if (isAuthenticated) {
    // Check if there's a returnTo path in location state
    const state = location.state as { returnTo?: string } | null;
    const redirectTo = state?.returnTo ?? ROUTES.USERS;
    return <Navigate to={redirectTo} replace />;
  }

  /**
   * Handle sign-in button click.
   * Initiates OIDC login flow via auth store.
   * The store handles loading state and errors internally.
   */
  const handleSignIn = () => {
    const returnTo = `${window.location.origin}${ROUTES.USERS}`;
    login(returnTo);
  };

  // Format error message if present
  const errorMessage = error ? formatErrorMessage(error) : null;

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3,
        }}
        data-testid="landing-page"
      >
        {/* ModernPath Branding */}
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          data-testid="landing-brand"
          sx={{ fontWeight: 600 }}
        >
          ModernPath
        </Typography>

        {/* Welcome Text / Tagline */}
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          data-testid="landing-title"
          sx={{ color: 'text.secondary' }}
        >
          Tenant Management Portal
        </Typography>

        <Typography
          variant="body1"
          data-testid="landing-subtitle"
          sx={{
            color: 'text.secondary',
            mb: 4,
            textAlign: 'center',
          }}
        >
          Sign in to manage your organization.
        </Typography>

        {/* Error Alert */}
        {errorMessage && (
          <Alert
            severity="error"
            sx={{ width: '100%', mb: 3 }}
            data-testid="landing-error"
          >
            {errorMessage}
          </Alert>
        )}

        {/* Sign In Button */}
        <Button
          variant="contained"
          size="large"
          onClick={handleSignIn}
          disabled={isLoading}
          data-testid="landing-sign-in-button"
          sx={{ minWidth: 200 }}
        >
          {isLoading ? (
            <CircularProgress
              size={24}
              color="inherit"
              data-testid="landing-loading"
            />
          ) : (
            'Sign in'
          )}
        </Button>
      </Box>
    </Container>
  );
}
